import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Table, Collapse } from "antd";
import { PlusOutlined, MinusOutlined } from "@ant-design/icons";
import "../css/orderItemsTable.css";
import { t } from "i18next";
import { buildCoFAUrl, buildDocUrl } from "../utils/techdocslink";

const OrderItemsTable = ({ orderData }) => {
  const [screenSize, setScreenSize] = useState(window.innerWidth);

  const orderItemsData = orderData?.orderItems;
  const salesOrg = orderData?.orderInfo?.salesorg;
  const trackingInfoData = orderData?.trackingInfos;

  const { Panel } = Collapse;

  // console.log("orderItemsData", orderItemsData)
  // console.log("screenSize", screenSize)

  const otherCountries = process.env.REACT_APP_Other_Countries.split("|");
  const baseURL = process.env.REACT_APP_Techdocs_URL;

  const generateCoFAUrl = useCallback(
    (materialNumber, batch) => buildCoFAUrl(baseURL, materialNumber, batch),
    [baseURL],
  );

  const generateDocUrl = useCallback(
    (materialNumber) => buildDocUrl(baseURL, materialNumber),
    [baseURL],
  );
  useEffect(() => {
    window.addEventListener("resize", () => {
      setScreenSize(window.innerWidth);
    });

    return () => {
      window.removeEventListener("resize", () => {
        setScreenSize(window.innerWidth);
      });
    };
  }, []);

  const trackingInfoColumn = useMemo(
    () => [
      {
        title: t("carrier"),
        dataIndex: "carrierDesc",
        key: "carrierDesc",
      },
      ...(salesOrg && !otherCountries.includes(salesOrg)
        ? [
            {
              title: t("trackingId"),
              dataIndex: "trackingID",
              key: "trackingID",

              onCell: (record, rowIndex) => {
                if (record.url) {
                  return { className: "clickStyle" };
                } else {
                  return { className: "removeClickStyle" };
                }
              },
              render: (text, record) => {
                return (
                  <a
                    href={record.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {record.trackingID.split(",").join(" ")}
                  </a>
                );
              },
            },
            {
              title: t("shipmentQty"),
              dataIndex: "trackingQTY",
              key: "trackingQTY",
            },
            {
              title: t("shipmentStatus"),
              dataIndex: "trackingStatus",
              key: "trackingStatus",
              render: (status) => {
                // We handle cases where status might be null or undefined
                const statusClass = status ? status.toLowerCase() : "";
                return (
                  <span className={`status-pill ${statusClass}`}>{status}</span>
                );
              },
            },
          ]
        : []),
    ],
    [salesOrg, otherCountries],
  );

  const orderItemsColumns = useMemo(
    () => [
      {
        title: t("item"),
        dataIndex: "posnr",
        key: "posnr",
        sortDirections: ["ascend", "descend", "ascend"],
        sorter: (a, b) => a.posnr?.localeCompare(b.posnr),
        render: (text, record) => {
          return <>{String(parseInt(record.posnr))}</>;
        },
      },
      {
        title: t("matNo"),
        dataIndex: "matnr",
        key: "matnr",
        sortDirections: ["ascend", "descend", "ascend"],
        sorter: (a, b) => a.matnr?.localeCompare(b.matnr),
      },

      {
        title: t("description"),
        dataIndex: "itemDesc",
        key: "itemDesc",
        sortDirections: ["ascend", "descend", "ascend"],
        sorter: (a, b) => a.itemDesc?.localeCompare(b.itemDesc),
        // width:'8%'
      },
      {
        title: t("estDelDate"),
        dataIndex: "estimatedDeliveryDate",
        key: "estimatedDeliveryDate",
        sortDirections: ["ascend", "descend", "ascend"],
        sorter: (a, b) => {
          console.log("aaaa", a, b);
          if (a.estimatedDeliveryDate) {
            return (
              new Date(a.estimatedDeliveryDate) -
              new Date(b.estimatedDeliveryDate)
            );
          } else {
            return (
              new Date(a.requestedDeliveryDate) -
              new Date(b.requestedDeliveryDate)
            );
          }
        },
        render: (text, record) => {
          return (
            <div>
              {record.estimatedDeliveryDate &&
              record.estimatedDeliveryDate !== "0000-00-00" &&
              record.orderStatus !== "Canceled" &&
              record.estimatedDeliveryDate !== null
                ? record.estimatedDeliveryDate
                : ""}
            </div>
          );
        },
        width: "8%",
      },
      {
        title: t("orderQty"),
        dataIndex: "qty",
        key: "qty",
        sortDirections: ["ascend", "descend", "ascend"],
        sorter: (a, b) => a.qty - b.qty,
        width: "6%",
      },
      {
        title: t("uom"),
        dataIndex: "unit",
        key: "unit",
        sortDirections: ["ascend", "descend", "ascend"],
        sorter: (a, b) => a.unit?.localeCompare(b.unit),
      },
      {
        title: t("backQty"),
        dataIndex: "backOrderQty",
        key: "backOrderQty",
        sortDirections: ["ascend", "descend", "ascend"],
        sorter: (a, b) => a.backOrderQty - b.backOrderQty,
        width: "6%",
      },
      {
        title: t("shpQty"),
        dataIndex: "delQty",
        key: "delQty",
        sortDirections: ["ascend", "descend", "ascend"],
        sorter: (a, b) => a.delQty - b.delQty,
        width: "6%",
      },
      {
        title: t("cnlQty"),
        dataIndex: "cancelQty",
        key: "cancelQty",
        sortDirections: ["ascend", "descend", "ascend"],
        sorter: (a, b) => a.qty - b.qty,
        width: "6%",
      },
      {
        title: t("batch"),
        dataIndex: "batch",
        key: "batch",
        sorter: (a, b) => a.batch?.localeCompare(b.batch),
      },
      {
        title: t("Techdocs"),
        key: "Techdocs",
        sortDirections: ["ascend", "descend", "ascend"],
        sorter: (a, b) => a.qty - b.qty,
        width: "6%",
        render: (_, record) => {
          const batch = record?.batch?.trim();
          const coFaUrl = generateCoFAUrl(record.matnr, record.batch);
          const docUrl = generateDocUrl(record?.matnr);
          return (
            <div
              style={{
                display: batch ? "flex" : "inline",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {/* Batch value */}

              {/* CoFA link (only if batch exists) */}
              {batch && (
                <a href={coFaUrl} target="_blank" rel="noopener noreferrer">
                  CoA
                </a>
              )}
              {/* Tech Docs link */}
              <a href={docUrl} target="_blank" rel="noopener noreferrer">
                DoC
              </a>
            </div>
          );
        },
      },
      {
        title: t("status"),
        dataIndex: "orderStatus",
        key: "orderStatus",
        sortDirections: ["ascend", "descend", "ascend"],
        sorter: (a, b) => a.orderStatus?.localeCompare(b.orderStatus),
        render: (status) => {
          // We handle cases where status might be null or undefined
          const statusClass = status
            ? status.toLowerCase().replace(/\s+/g, "-")
            : "";
          return <span className={`status-pill ${statusClass}`}>{status}</span>;
        },
      },
      ...((trackingInfoData && trackingInfoData.length) > 0
        ? [
            {
              title: t("trackingInfo"),
              dataIndex: "trackinginfo",
              key: "trackinginfo",
              render: (text, record) => {
                console.log("record", record);
                const filteredData = trackingInfoData.filter((tiRecord) => {
                  return tiRecord.orderItem === record.posnr;
                });

                return (
                  filteredData.length > 0 && (
                    <Collapse
                      defaultActiveKey={["0"]}
                      style={{ border: "none" }}
                      expandIcon={({ isActive }) =>
                        isActive ? (
                          <MinusOutlined
                            title="Minimise Tracking Information"
                            style={{ fontSize: "15px", margin: "auto" }}
                          />
                        ) : (
                          <PlusOutlined
                            title="Expand Tracking Information"
                            style={{
                              fontSize: "15px",
                              margin: "auto",
                              color: "#774DEF",
                            }}
                          />
                        )
                      }
                    >
                      <Panel header="" className="panel" key="0">
                        <Table
                          id="trackingInfo-table"
                          size="small"
                          style={{ width: "100%" }}
                          className={`trackingInfoTableClass ${salesOrg && otherCountries.includes(salesOrg) && "testTable"}`}
                          columns={trackingInfoColumn}
                          dataSource={filteredData}
                          rowKey={(record) =>
                            record.orderItem + record.trackingID
                          }
                          pagination={
                            filteredData?.length > 5
                              ? {
                                  showSizeChanger: false,
                                  pageSize: 5,
                                  size: "small",
                                }
                              : false
                          }
                        />
                      </Panel>
                    </Collapse>
                  )
                );
              },
            },
          ]
        : []),
    ],
    [trackingInfoData],
  );

  const orderMobileItemsColumns = [
    {
      title: "",
      key: "mobile",
      // sortDirections: ['ascend', 'descend', 'ascend'],
      // sorter: (a, b) => a.posnr?.localeCompare(b.posnr),
      render: (record) => {
        const filteredData = trackingInfoData.filter((tiRecord) => {
          return tiRecord.orderItem === record.posnr;
        });
        return (
          <div>
            <table className="mobileTable">
              <tbody>
                <tr>
                  <td align="left" className="mobileHeading">
                    {t("item")}
                  </td>
                  <td align="left">{record.posnr}</td>
                </tr>
                <tr>
                  <td align="left" className="mobileHeading">
                    {t("matNo")}
                  </td>
                  <td align="left">{record.matnr}</td>
                </tr>
                <tr>
                  <td align="left" className="mobileHeading">
                    {t("description")}
                  </td>
                  <td align="left">{record.itemDesc}</td>
                </tr>
                <tr>
                  <td align="left" className="mobileHeading">
                    {t("estDelDate")}
                  </td>
                  <td align="left">
                    {record.estimatedDeliveryDate &&
                    record.estimatedDeliveryDate !== "0000-00-00" &&
                    record.orderStatus !== "Canceled" &&
                    record.estimatedDeliveryDate !== null
                      ? record.estimatedDeliveryDate
                      : ""}
                  </td>
                </tr>
                <tr>
                  <td align="left" className="mobileHeading">
                    {t("orderQty")}
                  </td>
                  <td align="left">{record.qty}</td>
                </tr>
                <tr>
                  <td align="left" className="mobileHeading">
                    {t("uom")}
                  </td>
                  <td align="left">{record.unit}</td>
                </tr>
                <tr>
                  <td align="left" className="mobileHeading">
                    {t("backQty")}
                  </td>
                  <td align="left">{record.backOrderQty}</td>
                </tr>
                <tr>
                  <td align="left" className="mobileHeading">
                    {t("shpQty")}
                  </td>
                  <td align="left">{record.delQty}</td>
                </tr>
                <tr>
                  <td align="left" className="mobileHeading">
                    {t("cnlQty")}
                  </td>
                  <td align="left">{record.cancelQty}</td>
                </tr>
                <tr>
                  <td align="left" className="mobileHeading">
                    {t("batch")}
                  </td>
                  <td align="left">{record.batch ? record.batch : ""}</td>
                </tr>
                <tr>
                  <td align="left" className="mobileHeading">
                    {t("Techdocs")}
                  </td>
                  <td align="left">
                    <div
                      style={{
                        display: record.batch ? "flex" : "inline",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      {/* CoFA URL */}
                      {record.batch && (
                        <a
                          href={generateCoFAUrl(record.matnr, record.batch)}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "#774DEF", fontWeight: "bold" }}
                        >
                          CoA
                        </a>
                      )}
                      {/* DoC URL */}
                      <a
                        href={generateDocUrl(record.matnr)}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#774DEF", fontWeight: "bold" }}
                      >
                        DoC
                      </a>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td align="left" className="mobileHeading">
                    {t("status")}
                  </td>
                  <td align="left" className="orderStatus">
                    <span
                      className={`status-pill ${record.orderStatus.toLowerCase()}`}
                    >
                      {record.orderStatus}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
            {trackingInfoData &&
            trackingInfoData.length > 0 &&
            filteredData.length > 0 ? (
              <>
                <p
                  className="mobileHeading"
                  align="left"
                  style={{ marginTop: "5px", marginBottom: "0px" }}
                >
                  {t("trackingInfo")}
                </p>
                <Collapse
                  defaultActiveKey={["0"]}
                  style={{ border: "none", marginBottom: "40px" }}
                  expandIcon={({ isActive }) =>
                    isActive ? (
                      <MinusOutlined
                        title="Minimise Tracking Information"
                        style={{ fontSize: "15px", margin: "auto" }}
                      />
                    ) : (
                      <PlusOutlined
                        title="Expand Tracking Information"
                        style={{
                          fontSize: "15px",
                          margin: "auto",
                          color: "#774DEF",
                        }}
                      />
                    )
                  }
                >
                  <Panel header="" className="panel panel-mobile" key="0">
                    <Table
                      id="trackingInfo-table-mobile"
                      size="small"
                      style={{ width: "100%" }}
                      columns={trackingInfoColumn}
                      dataSource={filteredData}
                      rowKey={(record) => record.orderItem + record.trackingID}
                      pagination={
                        filteredData?.length > 5
                          ? { pageSize: 5, size: "small" }
                          : false
                      }
                    />
                  </Panel>
                </Collapse>
              </>
            ) : (
              <div style={{ marginBottom: "50px" }}></div>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="trackingInfoTable">
      <Table
        id="orderItem-table"
        size="large"
        style={{ width: "100%" }}
        columns={screenSize > 767 ? orderItemsColumns : orderMobileItemsColumns}
        dataSource={orderItemsData}
        rowKey="posnr"
        bordered
        pagination={{
          size: "small",
          pageSizeOptions: [10, 20, 50, 100],
          showSizeChanger: false,
          showTotal: (total, range) =>
            `Page ${range[0]} - ${range[1]} of ${total} `,
          position: ["bottomCenter"],
        }}
      />
    </div>
  );
};

export default OrderItemsTable;
